import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Calendar } from 'primereact/calendar';
import '../css/Pages.css';

const AnimatedCardWrapper = ({ isHidden, children }) => {
    return (
        <div style={{
            flex: isHidden ? '0 0 0px' : '1 1 220px',
            maxWidth: isHidden ? '0px' : '100%',
            margin: isHidden ? '0px' : '0 10px 20px 10px',
            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            zIndex: isHidden ? 0 : 1,
        }}>
            <div style={{
                position: isHidden ? 'absolute' : 'relative',
                top: 0, left: 0,
                width: '100%',
                minWidth: '220px',
                height: '100%',
                opacity: isHidden ? 0 : 1,
                transform: isHidden ? 'scale(0.9) translateX(-20px)' : 'scale(1) translateX(0)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                pointerEvents: isHidden ? 'none' : 'auto'
            }}>
                {children}
            </div>
        </div>
    );
};

const KpiCardItem = ({ card, onClick, actionLabel, actionIcon }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onClick}
            style={{
                backgroundColor: card.bg,
                padding: '20px',
                borderRadius: '12px',
                height: '100%',
                boxSizing: 'border-box',
                boxShadow: isHovered
                    ? '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)'
                    : '0 2px 4px rgba(0,0,0,0.05)',
                transform: isHovered ? 'translateY(-5px)' : 'translateY(0)',
                transition: 'all 0.3s ease, opacity 0.3s ease',
                position: 'relative',
                cursor: onClick ? 'pointer' : 'default',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <i className={`pi ${card.icon}`} style={{ fontSize: '1.5rem', color: card.color }}></i>

                {actionLabel ? (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: isHovered ? card.color : 'transparent',
                        color: isHovered ? '#fff' : card.color,
                        padding: '4px 12px',
                        borderRadius: '20px',
                        border: `1px solid ${isHovered ? 'transparent' : card.color}`,
                        transition: 'all 0.3s ease',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap'
                    }}>
                        <span>{actionLabel}</span>
                        <i className={`pi ${actionIcon}`}></i>
                    </div>
                ) : (
                    <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: isHovered ? card.color : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease'
                    }}>
                        <i className={isHovered ? "pi pi-arrow-right" : "pi pi-arrow-up-right"}
                            style={{ color: isHovered ? '#fff' : '#888', fontSize: '14px', transition: 'all 0.3s ease' }}>
                        </i>
                    </div>
                )}
            </div>

            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333', marginBottom: '20px', minHeight: '40px', whiteSpace: 'nowrap' }}>
                {card.title}
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px', whiteSpace: 'nowrap' }}>
                <span style={{ fontSize: '28px', fontWeight: '900', color: '#111827' }}>
                    {card.count.toLocaleString('vi-VN')}
                </span>
            </div>

            <div style={{ display: 'flex', gap: '5px', marginTop: '15px' }}>
                {[1, 2, 3, 4].map(i => (
                    <div key={i} style={{
                        height: '6px',
                        width: '20%',
                        backgroundColor: card.color,
                        opacity: isHovered ? 0.4 : 0.2,
                        borderRadius: '3px',
                        transition: 'opacity 0.3s ease'
                    }}></div>
                ))}
            </div>
        </div>
    );
};

const KpiCardGroup = ({ cards }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const articleCard = cards.find(c => c.detailStats);
    const otherCards = cards.filter(c => !c.detailStats);

    return (
        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            margin: '0 -10px',
            marginBottom: '10px',
            alignItems: 'stretch'
        }}>
            <AnimatedCardWrapper isHidden={false}>
                <KpiCardItem
                    card={{
                        title: isExpanded ? 'Bài báo Quốc tế' : 'Bài báo khoa học',
                        count: isExpanded ? articleCard.detailStats.intl : articleCard.count,
                        bg: '#e0f2fe',
                        color: '#0284c7',
                        icon: isExpanded ? 'pi-globe' : 'pi-file'
                    }}
                    onClick={toggleExpand}
                    actionLabel={isExpanded ? "Thu gọn" : "Mở rộng"}
                    actionIcon={isExpanded ? "pi-angle-double-left" : "pi-angle-double-right"}
                />
            </AnimatedCardWrapper>

            <AnimatedCardWrapper isHidden={!isExpanded}>
                <KpiCardItem
                    card={{
                        title: 'Bài báo Trong nước',
                        count: articleCard.detailStats.domestic,
                        bg: '#ccfbf1',
                        color: '#0d9488',
                        icon: 'pi-file'
                    }}
                    onClick={toggleExpand}
                    actionLabel="Thu gọn"
                    actionIcon="pi-angle-double-left"
                />
            </AnimatedCardWrapper>

            {otherCards.map((card, index) => (
                <AnimatedCardWrapper key={`other-${index}`} isHidden={false}>
                    <KpiCardItem card={card} />
                </AnimatedCardWrapper>
            ))}
        </div>
    );
};

const Dashboard = () => {
    const [isLoading, setIsLoading] = useState(true);

    const [rawData, setRawData] = useState({
        articles: [], topics: [], books: [], others: [], users: [], researchFields: [], departments: [],
        degrees: [], academicTitles: []
    });

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [kpiFilterYear, setKpiFilterYear] = useState(null);

    const [activeTab, setActiveTab] = useState('Công bố');
    const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);

    const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
    const authHeaders = useMemo(() => ({
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        'Content-Type': 'application/json'
    }), []);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setIsLoading(true);
        const baseUrl = process.env.REACT_APP_API_BASE_URL;

        try {
            const fetchUserWithCache = async () => {
                const cachedUsers = sessionStorage.getItem('cached_advanced_users');
                if (cachedUsers) return JSON.parse(cachedUsers);

                const res = await fetch(`${baseUrl}/user?mode=advanced_selection`, { headers: authHeaders });
                if (res.ok) {
                    const data = await res.json();
                    sessionStorage.setItem('cached_advanced_users', JSON.stringify(data));
                    return data;
                }
                return [];
            };

            const [resArt, resTopic, resBook, resOther, usersData, resFields, resDepts, resDegrees, resTitles] = await Promise.all([
                fetch(`${baseUrl}/scientificarticlemngt?userId=${currentUser.Id}&isAdmin=true`, { headers: authHeaders }),
                fetch(`${baseUrl}/scientifictopic?userId=${currentUser.Id}&isAdmin=true`, { headers: authHeaders }),
                fetch(`${baseUrl}/bookpublication?userId=${currentUser.Id}&isAdmin=true`, { headers: authHeaders }),
                fetch(`${baseUrl}/othersciencedeclaration?userId=${currentUser.Id}&isAdmin=true`, { headers: authHeaders }),
                fetchUserWithCache(),
                fetch(`${baseUrl}/researchfield`, { headers: authHeaders }),
                fetch(`${baseUrl}/department?mode=selection`, { headers: authHeaders }),
                fetch(`${baseUrl}/educationlevel`, { headers: authHeaders }),
                fetch(`${baseUrl}/jobtitle`, { headers: authHeaders })
            ]);

            const articles = await resArt.json().catch(() => []);
            const topics = await resTopic.json().catch(() => []);
            const books = await resBook.json().catch(() => []);
            const others = await resOther.json().catch(() => []);
            const fieldsData = await resFields.json().catch(() => []);
            const deptsData = await resDepts.json().catch(() => []);
            const degreesData = await resDegrees.json().catch(() => []);
            const titlesData = await resTitles.json().catch(() => []);

            setRawData({
                articles: Array.isArray(articles) ? articles : [],
                topics: Array.isArray(topics) ? topics : [],
                books: Array.isArray(books) ? books : [],
                others: Array.isArray(others) ? others : [],
                users: Array.isArray(usersData) ? usersData : [],
                researchFields: Array.isArray(fieldsData) ? fieldsData : [],
                departments: Array.isArray(deptsData) ? deptsData : [],
                degrees: Array.isArray(degreesData) ? degreesData : [],
                academicTitles: Array.isArray(titlesData) ? titlesData : []
            });

        } catch (error) {
            console.error("Lỗi tải dashboard:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const kpiFilteredData = useMemo(() => {
        const filterFn = (item) => {
            if (!kpiFilterYear) return true;

            if (item.TopicCode !== undefined || item.TopicLevel !== undefined) {
                if (!item.AcceptanceDate) return false;
                return new Date(item.AcceptanceDate).getFullYear() === new Date(kpiFilterYear).getFullYear();
            }

            const dateStr = item.PublicationDate || item.ApplyDate || item.StartDate;
            if (!dateStr) return false;
            return new Date(dateStr).getFullYear() === new Date(kpiFilterYear).getFullYear();
        };

        return {
            articles: rawData.articles.filter(filterFn),
            topics: rawData.topics.filter(filterFn),
            books: rawData.books.filter(filterFn),
            others: rawData.others.filter(filterFn)
        };
    }, [rawData, kpiFilterYear]);

    const filteredData = useMemo(() => {
        const filterFn = (item) => {
            if (!startDate && !endDate) return true;

            const dateStr = item.PublicationDate || item.AcceptanceDate || item.StartDate || item.ApplyDate;
            if (!dateStr) return false;

            const d = new Date(dateStr);
            d.setHours(0, 0, 0, 0);

            if (startDate) {
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                if (d < start) return false;
            }

            if (endDate) {
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                if (d > end) return false;
            }

            return true;
        };

        return {
            articles: rawData.articles.filter(filterFn),
            topics: rawData.topics.filter(filterFn),
            books: rawData.books.filter(filterFn),
            others: rawData.others.filter(filterFn)
        };
    }, [rawData, startDate, endDate]);

    const checkIsInternational = useCallback((item) => {
        const removeAccents = (str) => {
            return str ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase() : '';
        };

        const typeStr = removeAccents(item.JournalType || '');
        const nameStr = removeAccents(item.JournalName || '');
        const catStr = removeAccents(item.JournalCategory || '');
        const titleStr = item.Title || '';

        const qRank = (item.QRanking || '').trim().toUpperCase();
        const catRaw = (item.JournalCategory || '').trim().toUpperCase();

        const hasVietnameseTitle = /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i.test(titleStr);

        const strictDomesticJournals = [
            'kinh te va phat trien', 'khoa hoc kinh te', 'kh & cn', 'khoa hoc va cong nghe',
            'dai hoc', 'ky yeu', 'quoc gia', 'dhdn', 'thuong mai', 'quan ly', 'phat trien kinh te',
            'journal of science and technology', 'science and technology development',
            'ho chi minh', 'vnu', 'kinh te va quan ly', 'tap chi vn'
        ];

        if (strictDomesticJournals.some(kw => nameStr.includes(kw))) return false;

        const isQ = ['Q1', 'Q2', 'Q3', 'Q4'];
        if (isQ.includes(qRank) || isQ.includes(catRaw)) return true;

        const strongIntlKeywords = ['scopus', 'scie', 'ssci', 'isi', 'esci', 'springer', 'emerald', 'elsevier', 'mdpi'];
        if (strongIntlKeywords.some(kw => nameStr.includes(kw) || typeStr.includes(kw))) return true;

        if (!hasVietnameseTitle) return true;
        return false;
    }, []);

    const kpiDataCounts = useMemo(() => {
        const processArticleTypes = (list) => {
            let intl = 0;
            let domestic = 0;
            list.forEach(item => {
                if (checkIsInternational(item)) intl++;
                else domestic++;
            });
            return { intl, domestic };
        };
        return {
            articleTypes: processArticleTypes(kpiFilteredData.articles)
        };
    }, [kpiFilteredData, checkIsInternational]);

    const dataCounts = useMemo(() => {
        const processStatus = (list) => {
            return {
                keKhai: list.filter(item => item.Status === 'Lưu nháp' || item.Status === 'Đã gửi duyệt').length,
                daDuyet: list.filter(item => item.Status === 'Đã duyệt').length
            };
        };

        const processArticleTypes = (list) => {
            let intl = 0;
            let domestic = 0;
            list.forEach(item => {
                if (checkIsInternational(item)) intl++;
                else domestic++;
            });
            return { intl, domestic };
        };

        return {
            articles: processStatus(filteredData.articles),
            articleTypes: processArticleTypes(filteredData.articles),
            topics: processStatus(filteredData.topics),
            books: processStatus(filteredData.books),
            others: processStatus(filteredData.others)
        };
    }, [filteredData, checkIsInternational]);

    const kpiCards = useMemo(() => [
        {
            title: 'Bài báo khoa học',
            count: kpiFilteredData.articles.length,
            bg: '#e0f2fe', color: '#0284c7', icon: 'pi-file',
            detailStats: kpiDataCounts.articleTypes
        },
        {
            title: 'Đề tài NCKH',
            count: kpiFilteredData.topics.length,
            bg: '#dcfce7', color: '#16a34a', icon: 'pi-book'
        },
        {
            title: 'Sách - Giáo trình',
            count: kpiFilteredData.books.length,
            bg: '#ffedd5', color: '#ea580c', icon: 'pi-bookmark'
        },
        {
            title: 'Nội dung NCKH khác',
            count: kpiFilteredData.others.length,
            bg: '#f3e8ff', color: '#9333ea', icon: 'pi-box'
        }
    ], [kpiFilteredData, kpiDataCounts.articleTypes]);

    const publicationYearlyData = useMemo(() => {
        const yearCount = {};
        filteredData.articles.filter(item => item.Status === 'Đã duyệt' && item.PublicationDate).forEach(item => {
            const year = new Date(item.PublicationDate).getFullYear();
            if (year && year >= 2016) {
                yearCount[year] = (yearCount[year] || 0) + 1;
            }
        });
        const sortedYears = Object.keys(yearCount).sort();
        return {
            labels: sortedYears,
            data: sortedYears.map(year => yearCount[year])
        };
    }, [filteredData.articles]);

    const publicationLineChartData = {
        labels: publicationYearlyData.labels,
        datasets: [{
            label: 'Số lượng xuất bản', data: publicationYearlyData.data, fill: true, borderColor: '#1e40af',
            backgroundColor: 'rgba(30, 64, 175, 0.1)', tension: 0.4, pointBackgroundColor: '#1e40af',
            pointBorderColor: '#ffffff', pointBorderWidth: 2, pointRadius: 6, pointHoverRadius: 8
        }]
    };

    const publicationLineChartOptions = {
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { backgroundColor: '#1e293b', padding: 12, displayColors: false } },
        scales: { x: { grid: { display: false }, ticks: { font: { weight: 'bold' } } }, y: { beginAtZero: true, border: { display: false }, grid: { color: '#e2e8f0' }, ticks: { precision: 0 } } }
    };

    const researchFieldData = useMemo(() => {
        const fieldCount = {};
        filteredData.articles.filter(item => item.Status === 'Đã duyệt').forEach(item => {
            if (item.ResearchFieldId) {
                fieldCount[item.ResearchFieldId] = (fieldCount[item.ResearchFieldId] || 0) + 1;
            }
        });

        const mappedData = Object.entries(fieldCount).map(([id, count]) => {
            const fieldObj = rawData.researchFields.find(f => f.Id.toString() === id.toString());
            return { name: fieldObj ? fieldObj.Name : 'Chưa phân loại', count: count };
        });

        mappedData.sort((a, b) => b.count - a.count);
        const top10Data = mappedData.slice(0, 10);

        return {
            labels: top10Data.map(d => d.name),
            data: top10Data.map(d => d.count)
        };
    }, [filteredData.articles, rawData.researchFields]);

    const barColors = ['#1d4ed8', '#0ea5e9', '#d97706', '#be123c', '#8b5cf6', '#10b981', '#1e3a8a', '#f59e0b', '#b45309', '#e11d48'];
    const fieldBarChartData = {
        labels: researchFieldData.labels,
        datasets: [{ label: 'Số lượng', data: researchFieldData.data, backgroundColor: researchFieldData.labels.map((_, idx) => barColors[idx % barColors.length]), borderRadius: 4, barThickness: 15 }]
    };

    const fieldBarChartOptions = {
        maintainAspectRatio: false, indexAxis: 'y',
        plugins: { legend: { display: false }, tooltip: { backgroundColor: '#1e293b', padding: 12, displayColors: false } },
        scales: { x: { beginAtZero: true, grid: { color: '#e2e8f0' }, border: { display: false }, ticks: { precision: 0 } }, y: { grid: { display: false }, ticks: { font: { weight: '600' }, color: '#334155' } } }
    };

    const getTargetDepartments = useCallback((depts) => {
        const targetDept = depts.find(d => d.Name.toLowerCase().includes('kinh tế'));

        if (targetDept) {
            const children = depts.filter(d => d.ParentId === targetDept.Id);
            if (children.length > 0) return children;
        }

        const rootDepts = depts.filter(d => !d.ParentId);
        if (rootDepts.length === 1) {
            const level2 = depts.filter(d => d.ParentId === rootDepts[0].Id);
            if (level2.length === 1) {
                return depts.filter(d => d.ParentId === level2[0].Id);
            }
            return level2;
        }

        return rootDepts.length > 0 ? rootDepts : depts;
    }, []);

    const orgChartData = useMemo(() => {
        const displayDepts = getTargetDepartments(rawData.departments);

        const getDeptFamilyIds = (parentId) => {
            let familyIds = [parentId];
            const children = rawData.departments.filter(d => d.ParentId === parentId);
            children.forEach(child => {
                familyIds = familyIds.concat(getDeptFamilyIds(child.Id));
            });
            return familyIds;
        };

        let rootNode = null;
        const targetDept = rawData.departments.find(d => d.Name.toLowerCase().includes('kinh tế'));
        if (targetDept) {
            const children = rawData.departments.filter(d => d.ParentId === targetDept.Id);
            if (children.length > 0) {
                rootNode = targetDept;
            }
        }

        if (!rootNode) {
            const rootDepts = rawData.departments.filter(d => !d.ParentId);
            if (rootDepts.length === 1) {
                rootNode = rootDepts[0];
            }
        }

        const approvedArticles = filteredData.articles.filter(a => a.Status === 'Đã duyệt');

        const resolveDeptId = (article) => {
            if (article.DepartmentName) {
                const matched = rawData.departments.find(d => d.Name.trim() === article.DepartmentName.trim());
                if (matched) return matched.Id;
            } else if (article.UserId) {
                const owner = rawData.users.find(u => u.Id.toString() === article.UserId.toString());
                if (owner && owner.DepartmentId) return owner.DepartmentId;
            }
            return null;
        };

        let trueTotal = 0;
        if (rootNode) {
            const rootFamilyIds = getDeptFamilyIds(rootNode.Id);
            approvedArticles.forEach(a => {
                const dId = resolveDeptId(a);
                if (dId && rootFamilyIds.includes(dId)) trueTotal++;
            });
        } else {
            trueTotal = approvedArticles.length;
        }

        const chartData = displayDepts.map(dept => {
            const familyIds = getDeptFamilyIds(dept.Id);
            let count = 0;
            approvedArticles.forEach(a => {
                const dId = resolveDeptId(a);
                if (dId && familyIds.includes(dId)) count++;
            });
            return { name: dept.Name, count };
        });

        const validData = chartData.filter(d => d.count > 0).sort((a, b) => b.count - a.count);

        const TOP_LIMIT = 5;
        let finalLabels = [];
        let finalData = [];

        if (validData.length > TOP_LIMIT) {
            const topDepts = validData.slice(0, TOP_LIMIT);
            const top5Count = topDepts.reduce((acc, curr) => acc + curr.count, 0);

            const othersCount = trueTotal - top5Count;

            finalLabels = topDepts.map(d => d.name);
            finalData = topDepts.map(d => d.count);

            finalLabels.push('Các đơn vị khác');
            finalData.push(othersCount);
        } else {
            finalLabels = validData.map(d => d.name);
            finalData = validData.map(d => d.count);

            const mappedCount = validData.reduce((acc, curr) => acc + curr.count, 0);
            if (trueTotal > mappedCount) {
                finalLabels.push('Các đơn vị khác');
                finalData.push(trueTotal - mappedCount);
            }
        }

        return {
            labels: finalLabels,
            data: finalData,
            total: trueTotal
        };
    }, [rawData.departments, filteredData.articles, rawData.users, getTargetDepartments]);

    const pieColors = ['#0056b3', '#e74c3c', '#d39e00', '#8b5cf6', '#17a2b8', '#6c757d'];
    const pieChartData = {
        labels: orgChartData.labels,
        datasets: [{ data: orgChartData.data, backgroundColor: orgChartData.labels.map((_, idx) => pieColors[idx % pieColors.length]), hoverBackgroundColor: orgChartData.labels.map((_, idx) => pieColors[idx % pieColors.length]) }]
    };

    const pieChartOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'right', labels: { usePointStyle: true, font: { family: 'Inter, sans-serif', size: 13 }, color: '#334155', padding: 20 } },
            tooltip: {
                backgroundColor: '#1e293b', padding: 12,
                callbacks: {
                    label: function (context) {
                        let label = context.label || '';
                        if (label) label += ': ';
                        if (context.parsed !== null) {
                            label += context.parsed + ' bài báo';
                            const percentage = Math.round((context.parsed / orgChartData.total) * 100);
                            label += ` (${percentage}%)`;
                        }
                        return label;
                    }
                }
            }
        }
    };

    const orgArticleChartData = useMemo(() => {
        const displayDepts = getTargetDepartments(rawData.departments);

        const getDeptFamilyIds = (parentId) => {
            let familyIds = [parentId];
            const children = rawData.departments.filter(d => d.ParentId === parentId);
            children.forEach(child => {
                familyIds = familyIds.concat(getDeptFamilyIds(child.Id));
            });
            return familyIds;
        };

        const chartData = displayDepts.map(dept => {
            const familyIds = getDeptFamilyIds(dept.Id);
            let articleCount = 0;

            filteredData.articles.filter(a => a.Status === 'Đã duyệt').forEach(article => {
                let resolvedDeptId = null;
                if (article.DepartmentName) {
                    const matchedDept = rawData.departments.find(d => d.Name.trim() === article.DepartmentName.trim());
                    if (matchedDept) resolvedDeptId = matchedDept.Id;
                } else if (article.UserId) {
                    const ownerObj = rawData.users.find(u => u.Id.toString() === article.UserId.toString());
                    if (ownerObj && ownerObj.DepartmentId) resolvedDeptId = ownerObj.DepartmentId;
                }

                if (resolvedDeptId && familyIds.includes(resolvedDeptId)) {
                    articleCount++;
                }
            });

            return { name: dept.Name, count: articleCount };
        });

        const validData = chartData.filter(d => d.count > 0).sort((a, b) => b.count - a.count).slice(0, 10);

        return {
            labels: validData.map(d => d.name),
            data: validData.map(d => d.count)
        };
    }, [rawData.departments, filteredData.articles, rawData.users, getTargetDepartments]);

    const orgArticleBarColors = ['#0056b3', '#17a2b8', '#d39e00', '#e83e8c', '#8b5cf6', '#20c997', '#0056b3', '#17a2b8', '#d39e00', '#e83e8c'];

    const orgArticleBarData = {
        labels: orgArticleChartData.labels,
        datasets: [{ label: 'Số lượng bài báo', data: orgArticleChartData.data, backgroundColor: orgArticleChartData.labels.map((_, idx) => orgArticleBarColors[idx % orgArticleBarColors.length]), borderRadius: 4, barThickness: 15 }]
    };

    const orgArticleBarOptions = {
        maintainAspectRatio: false, indexAxis: 'y',
        plugins: { legend: { display: false }, tooltip: { backgroundColor: '#1e293b', padding: 12, displayColors: false } },
        scales: { x: { beginAtZero: true, border: { display: false }, ticks: { precision: 0 } }, y: { grid: { display: false }, ticks: { font: { weight: '600' }, color: '#334155' } } }
    };

    const carouselCharts = {
        'Công bố': [
            {
                title: 'NĂM XUẤT BẢN (CÔNG BỐ NCKH)',
                content: (
                    publicationYearlyData.labels.length > 0 ? (
                        <Chart type="line" data={publicationLineChartData} options={publicationLineChartOptions} style={{ height: '100%', width: '100%' }} />
                    ) : (<div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Không có dữ liệu năm xuất bản</div>)
                )
            },
            {
                title: 'TOP 10 LĨNH VỰC NGHIÊN CỨU',
                content: (
                    researchFieldData.labels.length > 0 ? (
                        <Chart type="bar" data={fieldBarChartData} options={fieldBarChartOptions} style={{ height: '100%', width: '100%' }} />
                    ) : (<div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Không có dữ liệu lĩnh vực</div>)
                )
            }
        ],
        'Tổ chức': [
            {
                title: 'CƠ CẤU ĐƠN VỊ KHOA / PHÒNG',
                content: (
                    orgChartData.labels.length > 0 ? (
                        <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ flex: 1 }}>
                                <Chart type="pie" data={pieChartData} options={pieChartOptions} style={{ height: '100%', width: '100%' }} />
                            </div>
                            <div style={{ position: 'absolute', bottom: '10px', right: '20px', fontWeight: 'bold', fontSize: '16px', color: '#1e293b' }}>
                                Tổng: {orgChartData.total.toLocaleString('vi-VN')}
                            </div>
                        </div>
                    ) : (<div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Không có dữ liệu tổ chức</div>)
                )
            },
            {
                title: 'TOP 10 KHOA / PHÒNG (THEO CÔNG BỐ)',
                content: (
                    orgArticleChartData.labels.length > 0 ? (
                        <Chart type="bar" data={orgArticleBarData} options={orgArticleBarOptions} style={{ height: '100%', width: '100%' }} />
                    ) : (<div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Không có dữ liệu công bố theo đơn vị</div>)
                )
            }
        ]
    };

    const activeCharts = carouselCharts[activeTab] || [];

    const nextCarouselChart = () => {
        if (activeCharts.length > 0) setCurrentCarouselIndex((prev) => (prev + 1) % activeCharts.length);
    };

    const prevCarouselChart = () => {
        if (activeCharts.length > 0) setCurrentCarouselIndex((prev) => (prev - 1 + activeCharts.length) % activeCharts.length);
    };

    useEffect(() => {
        setCurrentCarouselIndex(0);
    }, [activeTab]);

    const topAuthors = useMemo(() => {
        const authorMap = {};

        filteredData.articles.filter(a => a.Status === 'Đã duyệt').forEach(art => {
            let hasMembers = false;

            try {
                if (art.MembersJSON) {
                    const members = JSON.parse(art.MembersJSON);
                    if (Array.isArray(members) && members.length > 0) {
                        hasMembers = true;
                        const processedKeys = new Set();

                        members.forEach(m => {
                            const userId = m.UserId || m.userId || m.Id || m.id;
                            const rawName = m.FullName || m.fullName || m.Email || m.email || "Ẩn danh";

                            const groupKey = userId ? `id_${userId}` : `name_${rawName.toLowerCase()}`;

                            if (!processedKeys.has(groupKey)) {
                                processedKeys.add(groupKey);

                                let displayName = rawName;
                                let department = "Đơn vị khác / Ngoài trường";

                                if (userId) {
                                    const matchedUser = rawData.users.find(u => u.Id?.toString() === userId.toString());
                                    if (matchedUser) {
                                        displayName = `${matchedUser.LastName || ''} ${matchedUser.FirstName || ''}`.trim() || rawName;
                                        department = matchedUser.DepartmentName || department;
                                    }
                                } else if (art.FullName === rawName) {
                                    department = art.DepartmentName || department;
                                }

                                if (!authorMap[groupKey]) {
                                    authorMap[groupKey] = { name: displayName, count: 0, department: department };
                                }
                                authorMap[groupKey].count += 1;
                            }
                        });
                    }
                }
            } catch (e) {
                console.warn("Lỗi parse MembersJSON:", e);
            }

            if (!hasMembers) {
                const userId = art.UserId;
                const rawName = art.FullName || art.CreatedByEmail || "Ẩn danh";
                const groupKey = userId ? `id_${userId}` : `name_${rawName.toLowerCase()}`;

                if (!authorMap[groupKey]) {
                    authorMap[groupKey] = {
                        name: rawName,
                        count: 0,
                        department: art.DepartmentName || "Đơn vị khác"
                    };
                }
                authorMap[groupKey].count += 1;
            }
        });

        return Object.values(authorMap)
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    }, [filteredData.articles, rawData.users]);

    const oldYearlyData = useMemo(() => {
        const processYears = (list) => {
            const years = {};
            list.filter(i => i.Status === 'Đã duyệt').forEach(item => {
                const dateStr = item.PublicationDate || item.AcceptanceDate || item.StartDate || item.ApplyDate;
                if (dateStr) {
                    const year = new Date(dateStr).getFullYear();
                    if (year >= 2016) {
                        years[year] = (years[year] || 0) + 1;
                    }
                }
            });
            return years;
        };

        return {
            articles: processYears(rawData.articles),
            topics: processYears(rawData.topics),
            books: processYears(rawData.books)
        };
    }, [rawData]);

    const allYears = useMemo(() => {
        const yearSet = new Set([
            ...Object.keys(oldYearlyData.articles),
            ...Object.keys(oldYearlyData.topics),
            ...Object.keys(oldYearlyData.books)
        ]);
        return Array.from(yearSet).sort();
    }, [oldYearlyData]);

    const hrData = useMemo(() => {
        const deptMap = {};
        rawData.users.forEach(u => {
            if (u.DepartmentName) {
                deptMap[u.DepartmentName] = (deptMap[u.DepartmentName] || 0) + 1;
            }
        });
        return Object.entries(deptMap).map(([name, val]) => ({ name, val }));
    }, [rawData.users]);

    const oldBarData = {
        labels: ['Bài báo', 'Đề tài', 'Sách - Giáo trình', 'Nội dung NCKH khác'],
        datasets: [
            { label: 'Kê khai', backgroundColor: '#3b82f6', data: [dataCounts.articles.keKhai, dataCounts.topics.keKhai, dataCounts.books.keKhai, dataCounts.others.keKhai] },
            { label: 'Đã duyệt', backgroundColor: '#f59e0b', data: [dataCounts.articles.daDuyet, dataCounts.topics.daDuyet, dataCounts.books.daDuyet, dataCounts.others.daDuyet] }
        ]
    };

    const oldAreaData = {
        labels: allYears,
        datasets: [
            {
                label: 'Bài báo',
                data: allYears.map(y => oldYearlyData.articles[y] || 0),
                fill: true,
                borderColor: '#3b82f6',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                tension: 0.4
            },
            {
                label: 'Đề tài',
                data: allYears.map(y => oldYearlyData.topics[y] || 0),
                fill: true,
                borderColor: '#10b981',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                tension: 0.4
            },
            {
                label: 'Sách - Giáo trình',
                data: allYears.map(y => oldYearlyData.books[y] || 0),
                fill: true,
                borderColor: '#f59e0b',
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                tension: 0.4
            }
        ]
    };

    const oldHrChartData = {
        labels: hrData.map(d => d.name),
        datasets: [{ label: 'Nhân sự', data: hrData.map(d => d.val), backgroundColor: '#a855f7' }]
    };

    const commonOptions = { maintainAspectRatio: false, responsive: true };
    const oldBarOptions = { ...commonOptions, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } };
    const oldAreaOptions = { ...commonOptions, plugins: { legend: { display: true } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } };
    const oldHrOptions = { ...commonOptions, indexAxis: 'y', plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, ticks: { precision: 0 } } } };

    if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', marginTop: '100px' }}><ProgressSpinner /></div>;

    const styles = {
        container: { padding: '20px', backgroundColor: '#f4f7f6', minHeight: '100vh', boxSizing: 'border-box' },
        header: { backgroundColor: '#1e3a8a', backgroundImage: 'linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%)', padding: '20px', borderRadius: '10px', color: '#fff', marginBottom: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
        row: { display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' },
        card: { flex: '1 1 45%', backgroundColor: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', minWidth: '300px', display: 'flex', flexDirection: 'column' },
        cardTitleContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' },
        cardTitleText: { margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#444', textTransform: 'uppercase' },
        dateFilterBox: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
        chartWrapper: { flex: 1, minHeight: '300px', position: 'relative' },
        authorItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' },
        avatarBox: { display: 'flex', alignItems: 'center', gap: '15px' },
        avatar: { width: '45px', height: '45px', borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '18px' },
        badge: { width: '32px', height: '32px', borderRadius: '50%', border: '2px solid #22c55e', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#f0fdf4' }
    };

    const renderDateFilter = () => (
        <div style={styles.dateFilterBox}>
            <Calendar
                value={startDate}
                onChange={(e) => setStartDate(e.value)}
                placeholder="Từ ngày"
                dateFormat="dd/mm/yy"
                showIcon
                showClear
                style={{ width: '150px' }}
                inputStyle={{ padding: '8px', fontSize: '13px' }}
            />
            <span style={{ color: '#888', fontWeight: 'bold' }}>-</span>
            <Calendar
                value={endDate}
                onChange={(e) => setEndDate(e.value)}
                placeholder="Đến ngày"
                dateFormat="dd/mm/yy"
                showIcon
                showClear
                style={{ width: '150px' }}
                inputStyle={{ padding: '8px', fontSize: '13px' }}
                minDate={startDate || undefined}
            />
        </div>
    );

    const tabsMenu = ['Công bố', 'Tổ chức'];

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={{ margin: 0, textTransform: 'uppercase', fontSize: '22px' }}>Thông tin tổng quan</h2>
                <p style={{ margin: '5px 0 0 0', opacity: 0.8, fontSize: '14px' }}>Hệ thống quản lý khoa học © 2026 - SCV</p>
            </div>

            <style>
                {`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translate(-50%, 5px); }
                    to { opacity: 1; transform: translate(-50%, 0); }
                }
                `}
            </style>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
                <Calendar
                    value={kpiFilterYear}
                    onChange={(e) => setKpiFilterYear(e.value)}
                    view="year"
                    dateFormat="yy"
                    placeholder="Lọc theo năm"
                    showIcon
                    showClear
                    style={{ width: '180px' }}
                    inputStyle={{ padding: '8px', fontSize: '13px' }}
                />
            </div>

            <KpiCardGroup cards={kpiCards} />

            <div style={{ marginBottom: '20px', marginTop: '40px' }}>
                <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', gap: '5px' }}>
                    {tabsMenu.map(tab => (
                        <div
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                padding: '12px 24px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '15px',
                                color: activeTab === tab ? '#1d4ed8' : '#64748b',
                                borderBottom: activeTab === tab ? '3px solid #1d4ed8' : '3px solid transparent',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {tab}
                        </div>
                    ))}
                </div>
            </div>

            {activeCharts.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '30px' }}>
                    <button
                        onClick={prevCarouselChart}
                        style={{
                            background: '#ffffff', border: '1px solid #1d4ed8', borderRadius: '50%',
                            width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', color: '#1d4ed8', fontSize: '18px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            opacity: activeCharts.length <= 1 ? 0.3 : 1, pointerEvents: activeCharts.length <= 1 ? 'none' : 'auto'
                        }}
                    >
                        <i className="pi pi-angle-left"></i>
                    </button>

                    <div style={{
                        flex: 1,
                        maxWidth: '1000px',
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        padding: '30px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                        border: '1px solid #f1f5f9'
                    }}>
                        <h3 style={{ textAlign: 'center', color: '#0f172a', fontSize: '20px', margin: '0 0 30px 0', textTransform: 'uppercase' }}>
                            {activeCharts[currentCarouselIndex].title}
                        </h3>
                        <div style={{ height: '400px', position: 'relative' }}>
                            {activeCharts[currentCarouselIndex].content}
                        </div>
                    </div>

                    <button
                        onClick={nextCarouselChart}
                        style={{
                            background: '#ffffff', border: '1px solid #1d4ed8', borderRadius: '50%',
                            width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', color: '#1d4ed8', fontSize: '18px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            opacity: activeCharts.length <= 1 ? 0.3 : 1, pointerEvents: activeCharts.length <= 1 ? 'none' : 'auto'
                        }}
                    >
                        <i className="pi pi-angle-right"></i>
                    </button>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8', backgroundColor: '#fff', borderRadius: '16px', marginBottom: '30px', border: '1px solid #f1f5f9' }}>
                    Chưa có dữ liệu thống kê cho mục này
                </div>
            )}

            <hr style={{ border: 'none', borderTop: '2px dashed #e2e8f0', margin: '40px 0' }} />

            <div style={styles.row}>
                <div style={{ ...styles.card, flex: '1.5 1 55%' }}>
                    <div style={styles.cardTitleContainer}>
                        <h3 style={styles.cardTitleText}>Số lượng công trình</h3>
                        {renderDateFilter()}
                    </div>
                    <div style={styles.chartWrapper}>
                        <Chart type="bar" data={oldBarData} options={oldBarOptions} style={{ height: '100%', width: '100%' }} />
                    </div>
                </div>

                <div style={{ ...styles.card, flex: '1 1 40%' }}>
                    <div style={styles.cardTitleContainer}>
                        <h3 style={styles.cardTitleText}>Top 5 công bố bài báo (Đã duyệt)</h3>
                        {renderDateFilter()}
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        {topAuthors.length > 0 ? topAuthors.map((author, index) => (
                            <div key={index} style={styles.authorItem}>
                                <div style={styles.avatarBox}>
                                    <div style={{ ...styles.avatar, backgroundColor: ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444'][index % 5] }}>
                                        {author.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 'bold', color: '#333', fontSize: '15px' }}>{author.name}</div>
                                        <div style={{ fontSize: '13px', color: '#888', marginTop: '3px' }}>{author.department}</div>
                                    </div>
                                </div>
                                <div style={styles.badge}>{author.count}</div>
                            </div>
                        )) : (
                            <p style={{ textAlign: 'center', color: '#888', marginTop: '20px' }}>Không có dữ liệu trong khoảng thời gian này</p>
                        )}
                    </div>
                </div>
            </div>

            <div style={styles.row}>
                <div style={{ ...styles.card, flex: '1 1 100%' }}>
                    <div style={styles.cardTitleContainer}>
                        <h3 style={styles.cardTitleText}>Số lượng công bố khoa học qua các năm</h3>
                    </div>
                    <div style={{ height: '350px', position: 'relative' }}>
                        <Chart type="line" data={oldAreaData} options={oldAreaOptions} style={{ height: '100%', width: '100%' }} />
                    </div>
                </div>
            </div>

            <div style={styles.row}>
                <div style={{ ...styles.card, flex: '1 1 100%' }}>
                    <div style={styles.cardTitleContainer}>
                        <h3 style={styles.cardTitleText}>Thống kê nhân sự theo đơn vị</h3>
                    </div>
                    <div style={{ height: '400px', position: 'relative' }}>
                        <Chart type="bar" data={oldHrChartData} options={oldHrOptions} style={{ height: '100%', width: '100%' }} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;